import { Flex, Text } from '@chakra-ui/react';
import { ButtonText, Select, useToast } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useManageSubmissionAssignment } from 'src/api/commands';
import { fetchSubmissionContextRoleAssignments } from 'src/api/queries';
import { useSubmissionData } from '../../contexts/SubmissionDataProvider';
import { FETCH_EDITORIAL_ASSIGNEES_KEY } from '../../constants';
import { useKeycloak } from 'src/components/AuthHOC';
import ProtectedWorkflowAction from 'src/components/ProtectedAction/ProtectedWorkflowAction';

interface Props {
  assigneeId?: string;
  submissionId: string;
}

export const SubmissionAssignment: React.FC<Props> = ({
  assigneeId,
  submissionId
}) => {
  const { currentUser } = useKeycloak();
  const toast = useToast();
  const { availableActions, refetchSubmission, organizationId } =
    useSubmissionData();
  const { ctxId = '' } = useParams();

  const { data: roleAssignments } = useQuery({
    queryKey: ['roles-assignments', ctxId],
    queryFn: () => fetchSubmissionContextRoleAssignments(ctxId)
  });

  const manageSubmissionAssignmentMutation = useManageSubmissionAssignment({
    submissionContextId: ctxId,
    submissionId,
    organizationId,
    subtype: 'owner',
    invalidateSubmissionQueries: refetchSubmission.including(
      FETCH_EDITORIAL_ASSIGNEES_KEY
    )
  });

  const onAssignSubmission = async (assigneeId?: string, role = 'owner') => {
    if (!assigneeId) return;

    const res = await manageSubmissionAssignmentMutation.mutateAsync({
      action: 'assign',
      assigneeId,
      role
    });

    if (res.isSuccess) {
      return toast({
        variant: 'success',
        title: 'Submission assigned to user',
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: res.message,
      isClosable: true
    });
  };

  const onUnassignSubmission = async (role = 'owner') => {
    const res = await manageSubmissionAssignmentMutation.mutateAsync({
      action: 'unassign',
      role
    });

    if (res.isSuccess) {
      return toast({
        variant: 'success',
        title: 'Submission unassigned',
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: res.message,
      isClosable: true
    });
  };

  const members =
    roleAssignments?.reduce(
      (acc, roleAssignment) => [...acc, ...roleAssignment.users],
      [] as { id: string; firstName: string; lastName: string }[]
    ) || [];

  const isDisabled =
    !members.length || !availableActions.contains('AssignSubmission#owner');

  const canAssignToMe =
    assigneeId !== currentUser?.id &&
    members?.findIndex(member => member.id === currentUser?.id) !== -1;

  const canUnassign =
    !!assigneeId && availableActions.contains('UnassignSubmission#owner');

  return (
    <Flex gap={2} alignItems={'center'}>
      <Text textStyle="headingItem">Owner</Text>

      <ProtectedWorkflowAction actionName="AssignSubmission#owner">
        <Select
          width="300px"
          data-testId="assign-to-dropdown"
          onChange={({ target: { value } }) => {
            onAssignSubmission(value);
          }}
        >
          <option selected={!assigneeId} disabled>
            --
          </option>
          {members.map(member => (
            <option
              key={member.id}
              value={member.id}
              selected={member.id === assigneeId}
            >
              {member.firstName} {member.lastName}
            </option>
          ))}
        </Select>
      </ProtectedWorkflowAction>
      {!isDisabled && (
        <Flex gap={2}>
          {canAssignToMe && (
            <ProtectedWorkflowAction actionName="AssignSubmission#owner">
              <ButtonText
                data-testId="assign-to-me-button"
                onClick={() => onAssignSubmission(currentUser?.id)}
              >
                Assign to Me
              </ButtonText>
            </ProtectedWorkflowAction>
          )}
          {canUnassign && (
            <ProtectedWorkflowAction actionName="UnassignSubmission#owner">
              <ButtonText
                data-testId="unassign-button"
                onClick={() => onUnassignSubmission()}
              >
                Unassign
              </ButtonText>
            </ProtectedWorkflowAction>
          )}
        </Flex>
      )}
    </Flex>
  );
};
